import React, { useDeferredValue, useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { type DropdownOption, LoadingIcon, Sheet, Wrapper } from '@pkg/components';
import Dropdown from '@pkg/components/Dropdown';
import { Input, Pagination, SearchWrapper } from '@pkg/components/Sections/Admin';
import { CollectionListItem } from '@pkg/components/Sections/Admin/Collections';
import { Header, ListTable } from '@pkg/components/Sections/Admin/Collections/List/styles';
import { StyledForm, StyledInputLabel } from '@pkg/components/Sections/Admin/components/Form/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useGetAdminCompetitionCollections, useUpdateCollectionStatus } from '@pkg/hooks';

import { CollectionChainType, CompetitionCollectionStatus, type SortingDir } from '@app/client';

export const AdminCollectionList = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [sortDirection, setSortDirection] = useState<SortingDir>('Asc');
    const { mutateAsync, isPending: isLoadingCollectionUpdate } = useUpdateCollectionStatus();

    const filterAll = {
        label: 'All',
        value: '',
    };
    const [statusFilter, setStatusFilter] = useState<DropdownOption>(filterAll);
    const [chain, setChain] = useState<CollectionChainType>(CollectionChainType.Ethereum);
    const [collectionSlug, setCollectionSlug] = useState('');
    const [updateSidebarOpen, setUpdateSidebarOpen] = useState(false);
    const [updateStatus, setUpdateStatus] = useState<CompetitionCollectionStatus>(CompetitionCollectionStatus.Normal);

    const deferredQuery = useDeferredValue(searchQuery);

    const {
        competitionCollections,
        isLoading,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetching,
        isFetchingGS,
        isSearching,
        pagingParams,
    } = useGetAdminCompetitionCollections(deferredQuery, statusFilter, sortDirection);

    const handleUpdateCollection = () => {
        if (collectionSlug) {
            mutateAsync({ collectionSlug, chain, value: { status: updateStatus } }).then(() => {
                setUpdateSidebarOpen(false);
                setSearchValue(collectionSlug);
                setSearchQuery(collectionSlug);
                setCollectionSlug('');
            });
        }
    };

    function handleSearch(e) {
        e.preventDefault();
        setSearchQuery(searchValue);
    }
    function handleStatus(type: DropdownOption) {
        setStatusFilter(type ?? undefined);
    }

    const chainOptions: DropdownOption[] = Object.values(CollectionChainType).map((chain) => ({
        value: chain,
        label: chain,
    }));

    const updateStatusOptions: DropdownOption[] = [
        ...Object.keys(CompetitionCollectionStatus).map((statusKey) => {
            return {
                label: CompetitionCollectionStatus[statusKey],
                value: statusKey,
            };
        }),
    ];

    const typeOptions: DropdownOption[] = [
        filterAll,
        ...Object.keys(CompetitionCollectionStatus).map((statusKey) => {
            return {
                label: CompetitionCollectionStatus[statusKey],
                value: statusKey,
            };
        }),
    ];

    const paginationMarkup = !isLoading && (
        <Pagination
            variant="buttoned"
            fetchNextPage={fetchNextPage}
            fetchPreviousPage={fetchPreviousPage}
            isFetching={isFetching || isFetchingGS}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            pagingParams={pagingParams}
        />
    );

    const listMarkup = useMemo(
        () => (
            <>
                <ListTable $headers>
                    <Header
                        $isSort
                        $isDesc={sortDirection === 'Desc'}
                        onClick={() => setSortDirection((c) => (c === 'Desc' ? 'Asc' : 'Desc'))}
                    >
                        Slug
                    </Header>
                    <Header>Status</Header>
                </ListTable>
                {competitionCollections?.length
                    ? competitionCollections.map((collection) => (
                          <CollectionListItem key={`${collection.id}-${collection.slug}`} item={collection} />
                      ))
                    : 'Nothing to see here 👀 - try another collection slug or status type'}
            </>
        ),
        [competitionCollections, sortDirection],
    );

    return (
        <>
            <Wrapper direction="column">
                <Wrapper>
                    <Button color="error" variant="solid" onClick={() => setUpdateSidebarOpen(true)}>
                        Update collection status by slug and chain
                    </Button>
                </Wrapper>
                <SearchWrapper>
                    <Wrapper direction="row" gap="10px" alignItems="center" width="200px">
                        Status:
                        <Dropdown
                            selected={statusFilter}
                            options={typeOptions}
                            onClick={(option) => handleStatus(option || '')}
                        />
                    </Wrapper>
                    <StyledForm onSubmit={handleSearch}>
                        <Input
                            placeholder="doodles-official,azuki"
                            name="search"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                    </StyledForm>
                    <Button type="submit" onClick={handleSearch} size="small" color="transparent">
                        Search
                    </Button>
                    <Button
                        type="button"
                        onClick={() => {
                            setSearchQuery('');
                            setSearchValue('');
                        }}
                        size="small"
                        color="clear"
                    >
                        Clear
                    </Button>
                </SearchWrapper>
                {isLoading || isSearching ? <LoadingIcon /> : listMarkup}
                {paginationMarkup}
            </Wrapper>
            <AnimatePresence>
                {updateSidebarOpen && (
                    <Sheet onClickOutside={() => setUpdateSidebarOpen(false)}>
                        <Sheet.Header title="Update collection" onClose={() => setUpdateSidebarOpen(false)} />
                        <Wrapper padding="20px 40px">
                            <Wrapper direction="row" margin="0 0 20px">
                                <Wrapper direction="row" gap="10px" alignItems="center" margin="15px 0">
                                    <StyledInputLabel>Chain:</StyledInputLabel>
                                    <Dropdown
                                        selected={{ label: chain, value: chain }}
                                        options={chainOptions}
                                        onClick={(option) => setChain(option.value as CollectionChainType)}
                                    />
                                </Wrapper>
                                <Wrapper direction="row" gap="10px" alignItems="center" margin="15px 0">
                                    <StyledInputLabel>Status:</StyledInputLabel>
                                    <Dropdown
                                        selected={{ label: updateStatus, value: updateStatus }}
                                        options={updateStatusOptions}
                                        onClick={(option) =>
                                            setUpdateStatus(option.value as CompetitionCollectionStatus)
                                        }
                                    />
                                </Wrapper>
                            </Wrapper>
                            <Wrapper gap="15px">
                                <StyledInputLabel>Collection slug to update</StyledInputLabel>
                                <Input value={collectionSlug} onChange={(e) => setCollectionSlug(e.target.value)} />
                            </Wrapper>
                            <Sheet.Footer>
                                <Wrapper
                                    direction="row"
                                    gap="30px"
                                    alignItems="center"
                                    justifyContent="center"
                                    margin="40px 0 15px"
                                >
                                    <Button
                                        color="primary"
                                        size="small"
                                        variant="solid"
                                        onClick={handleUpdateCollection}
                                        loading={isLoadingCollectionUpdate}
                                    >
                                        Update status
                                    </Button>
                                    <Button
                                        color="error"
                                        size="small"
                                        variant="solid"
                                        onClick={() => setUpdateSidebarOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                </Wrapper>
                            </Sheet.Footer>
                        </Wrapper>
                    </Sheet>
                )}
            </AnimatePresence>
        </>
    );
};
